import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../components/ui/Toast";
import { isOpenedAsPopup, postAuthResult } from "./authPopup";
import { takeLoginNext } from "../../utils/lastProduct";
import { useLanguage } from "../../components/ui/LanguageProvider";
import Spinner from "../../components/ui/Spinner";

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const called = useRef(false);
  const { t } = useLanguage();

  useEffect(() => {
    // OAuth code는 일회용 → 중복 교환 방지 (StrictMode 이중 실행/새로고침 등)
    if (called.current) return;
    called.current = true;

    // 팝업이면 부모창에 결과 전송 후 닫고, 아니면(전체 리다이렉트 폴백) 직접 라우팅
    const popup = isOpenedAsPopup();
    const succeed = () => {
      if (popup) postAuthResult({ type: "auth-success" });
      // 전체 리다이렉트 폴백: SPA navigate는 마운트 시점(쿠키 발급 전) verify 결과를 유지해
      // 로그인돼도 /login으로 튕긴다. 하드 로드로 AuthProvider 재마운트+재verify.
      else window.location.replace(takeLoginNext());
    };
    const fail = (message: string) => {
      if (popup) {
        postAuthResult({ type: "auth-error", message });
      } else {
        toast.show({ type: "error", message });
        navigate("/login", { replace: true });
      }
    };

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    if (!code) {
      fail(t("인증 코드가 없습니다.", "No authorization code."));
      return;
    }

    fetch("/api/auth/callback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, state }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || t("로그인에 실패했습니다.", "Sign-in failed."));
        succeed();
      })
      .catch((err) => fail(err.message));
  }, []);

  // 코드 교환 중(보통 1초 안팎) — 빈 화면 대신 「로그인 중…」 한 줄(DESIGN-AI §30). 끝나면 닫히거나 이동한다.
  return (
    <div role="status" aria-live="polite" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: "#fff", color: "#64748b", fontSize: 14 }}>
      <Spinner size={16} />
      {t("로그인 중…", "Signing in…")}
    </div>
  );
}
